import React from 'react';
import { Autocomplete, Box, TextField, Typography, createFilterOptions } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export interface CreatableSelectOption {
  value: string;
  /** Shown under the value in the list, e.g. a metafield's name and type. */
  hint?: string;
}

interface CreatableSelectProps {
  label: string;
  value: string;
  options: Array<string | CreatableSelectOption>;
  onChange: (value: string) => void;
  /** Fires when a listed option is picked, with that option (not on typing). */
  onPick?: (option: CreatableSelectOption) => void;
  placeholder?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  /** Offer a "Use <typed>" row for values not on the list. Default true. */
  allowCreate?: boolean;
  loading?: boolean;
  sx?: SxProps<Theme>;
  'aria-label'?: string;
}

type Row = CreatableSelectOption & { typed?: boolean };

const filter = createFilterOptions<Row>({ stringify: (row) => `${row.value} ${row.hint || ''}` });

const toRow = (option: string | CreatableSelectOption): Row =>
  typeof option === 'string' ? { value: option } : option;

/**
 * Pick from a list, or type a value that is not on it. Whatever is in the box
 * is the value — no Enter needed — so a typed override is never lost on blur.
 */
export const CreatableSelect: React.FC<CreatableSelectProps> = ({
  label,
  value,
  options,
  onChange,
  onPick,
  placeholder,
  helperText,
  error,
  disabled,
  allowCreate = true,
  loading,
  sx,
  'aria-label': ariaLabel,
}) => {
  const rows = React.useMemo(() => {
    const seen = new Set<string>();
    const out: Row[] = [];
    for (const option of options) {
      const row = toRow(option);
      if (!row.value || seen.has(row.value)) continue;
      seen.add(row.value);
      out.push(row);
    }
    return out;
  }, [options]);

  const selected = rows.find((row) => row.value === value) ?? (value ? { value } : null);

  return (
    <Autocomplete<Row, false, false, true>
      freeSolo
      size="small"
      disabled={disabled}
      // Above a Dialog, so the list opens over the modal it sits in.
      slotProps={{ popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 1 } } }}
      loading={loading}
      options={rows}
      value={selected}
      inputValue={value}
      onInputChange={(_, next, reason) => {
        // `reset` echoes the picked option's label, which onChange already set.
        if (reason === 'input' || reason === 'clear') onChange(next);
      }}
      onChange={(_, next) => {
        if (next === null) {
          onChange('');
          return;
        }
        const row = typeof next === 'string' ? { value: next } : next;
        onChange(row.value);
        if (typeof next !== 'string' && !row.typed && onPick) onPick(row);
      }}
      getOptionLabel={(row) => (typeof row === 'string' ? row : row.value)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={(list, state) => {
        const filtered = filter(list, state);
        const typed = state.inputValue.trim();
        if (allowCreate && typed && !list.some((row) => row.value.toLowerCase() === typed.toLowerCase())) {
          filtered.push({ value: typed, typed: true });
        }
        return filtered;
      }}
      renderOption={(props, row) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        return (
          <Box component="li" key={key} {...rest} sx={{ flexDirection: 'column', alignItems: 'flex-start !important' }}>
            <Typography variant="body2">{row.typed ? `Use "${row.value}"` : row.value}</Typography>
            {row.typed ? (
              <Typography variant="caption" color="text.secondary">Not in the list, kept as typed</Typography>
            ) : row.hint ? (
              <Typography variant="caption" color="text.secondary">{row.hint}</Typography>
            ) : null}
          </Box>
        );
      }}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': ariaLabel ?? label } }}
        />
      )}
    />
  );
};
